import {useTheme} from "@emotion/react";
import {useLoaderData} from "@remix-run/react";
import {motion} from "framer-motion";
import {useEffect, useRef, useState} from "react";
import Markdown from "../../../elements/Markdown";
import {H1} from "../../../elements/semanticWrappers";
import MouseScrollIcon from "../../../elements/svgicon/MouseScroll.jsx";
import {Text, textStyles} from "../../../elements/text";
import useHasHydrated from "../../../hooks/useHasHydrated.js";
import SectionWrapper from "../../../layouts/SectionWrapper";
import {
  GradientTop,
  Logo,
  LogoWrapper,
  ScrollToNextSection,
  TextContainer,
  TextWrapper,
  ToggleTextLength,
} from "./styles.js";

export default function HeroTransparent({data}) {
  const hydrated = useHasHydrated();
  const {translation} = useLoaderData();
  const seeMore = translation?.seeMore || "See more";
  const showLess = translation?.showLess || "Show less";
  const theme = useTheme();
  const {isMobile} = theme.breakpoints;
  const credit = isMobile ? data?.mobile_image?.caption : data?.desktop_image?.caption;

  const [isTextExpanded, setTextExpanded] = useState(false);
  const firstSectionRef = useRef(null);

  const scrollToNextSection = () => {
    const sectionHeight = firstSectionRef.current.clientHeight;
    window.scrollTo({top: sectionHeight, behavior: "smooth"});
  };

  const splitContent = data?.text?.split("<p><!--more--></p>");
  const previewContent = splitContent?.[0];
  const hasMoreContent = splitContent?.length > 1;

  const toggleTextRef = useRef(null);

  const smoothScroll = (element, duration) => {
    const start = window.pageYOffset;
    const target = element.getBoundingClientRect().top + start;
    const startTime = performance.now();

    const scroll = (currentTime) => {
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const ease = progress * (2 - progress);

      window.scrollTo(0, start + (target - start) * ease);

      if (timeElapsed < duration) {
        requestAnimationFrame(scroll);
      }
    };

    requestAnimationFrame(scroll);
  };
  const handleToggleTextLength = () => {
    if (isTextExpanded) {
      smoothScroll(toggleTextRef.current, 1000);
    }
    setTextExpanded((prev) => !prev);
  };

  return (
    <div ref={firstSectionRef}>
      <GradientTop />
      <SectionWrapper
        id={data?.id}
        bgImage={
          isMobile && !!data?.mobile_image?.url?.length
            ? data?.mobile_image?.url
            : data?.desktop_image?.url
        }
        styles={{
          height: isMobile ? 667 : 810,
        }}
        innerWrapperStyles={{
          display: "flex",
          height: "100%",
          minHeight: isMobile ? 667 : "auto",
          gridColumnGap: isMobile ? 0 : 10,
          alignItems: "center",
        }}
      >
        <div style={{width: "100%", gridColumn: "1/-1"}}>
          <H1
            styles={{
              width: "100%",
              textAlign: "center",
              marginTop: "auto",
              color: "white",
            }}
          >
            {data?.title}
          </H1>
        </div>

        {!isMobile && (
          <ScrollToNextSection onClick={scrollToNextSection}>
            <MouseScrollIcon />
          </ScrollToNextSection>
        )}

        <LogoWrapper>
          {!!data?.logo && <Logo imgUrl={data.logo} />}
          {!!credit && (
            <Text
              styles={{
                width: "100%",
                marginTop: isMobile ? 16 : 20,
                color: "rgba(255, 255, 255, 0.8)",
                whiteSpace: "nowrap",
                textAlign: "end",
                opacity: 0.8,
              }}
              type="textSmallRegular"
            >
              {credit}
            </Text>
          )}
        </LogoWrapper>
        <div ref={toggleTextRef} />
      </SectionWrapper>

      {!!data?.text?.length && (
        <SectionWrapper>
          <TextContainer>
            <Markdown
              styles={{
                margin: 0,
                backgroundColor: "white",
                zIndex: 1,
                ...textStyles(theme).titleExtraSmall,
              }}
            >
              {previewContent}
            </Markdown>
            {hasMoreContent && (
              <>
                <motion.div
                  animate={{height: isTextExpanded ? "auto" : "0px"}}
                  style={{overflow: "hidden"}}
                  transition={{duration: hydrated ? 0.8 : 0}}
                >
                  <TextWrapper isTextExpanded={isTextExpanded}>
                    <Markdown styles={{margin: 0, ...textStyles(theme).titleExtraSmall}}>
                      {splitContent[1]}
                    </Markdown>
                  </TextWrapper>
                </motion.div>
                <ToggleTextLength onClick={(e) => handleToggleTextLength(e)}>
                  {isTextExpanded ? showLess : seeMore}
                </ToggleTextLength>
              </>
            )}
          </TextContainer>
        </SectionWrapper>
      )}
    </div>
  );
}
